import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { IxcRegistrationV1AccountState } from '@interxfi/types/generated/api';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { getRegistrationAccountStateListItems } from '../../../../../../../../../features/registration/account/registration-account-list-items';
import { RegistrationAccountOverviewTabbedNavigation } from '../../../../../../../../../features/registration/account/registration-account-overview-tabbed-navigation';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_overview/detail/')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-details`,
        label: 'Account Details',
        to: `/registration/account/${params.accountId}/detail`,
      }),
    }) satisfies AggregateBreadcrumb,
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountOverview,
      label: 'Overview',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountOverview>,
  head: () => getPageHead('Account Details'),
  component: AccountDetailOverview,
});

function getPurposeListItems(items: ReturnType<typeof getRegistrationAccountStateListItems>) {
  if ('platform' in items.data.spec) {
    return [
      items.data.spec.platform.value.nickname,
      { ...items.data.spec.platform.value.owner.typeName, title: 'Owner Type' },
      ...('program' in items.data.spec.platform.value.owner
        ? [
            items.data.spec.platform.value.owner.program.value.legalName,
            items.data.spec.platform.value.owner.program.value.taxIdentifier,
            items.data.spec.platform.value.owner.program.value.companyAddress.formattedMultiLine,
            items.data.spec.platform.value.owner.program.value.mailingAddress.formattedMultiLine,
            items.data.spec.platform.value.owner.program.value.exemptPayeeCode,
          ]
        : []),
      ...('profile' in items.data.spec.platform.value.owner
        ? [
            items.data.spec.platform.value.owner.profile.value.profileId,
            items.data.spec.platform.value.owner.profile.value.mergedName,
            items.data.spec.platform.value.owner.profile.value.phone,
            items.data.spec.platform.value.owner.profile.value.email,
            items.data.spec.platform.value.owner.profile.value.companyAddress.formatted,
            items.data.spec.platform.value.owner.profile.value.mailingAddress.formatted,
            items.data.spec.platform.value.owner.profile.value.entityType,
            items.data.spec.platform.value.owner.profile.value.businessClassification,
            items.data.spec.platform.value.owner.profile.value.countryOfFormation,
            ...(items.data.spec.platform.value.owner.profile.value.stateOfFormation
              ? [items.data.spec.platform.value.owner.profile.value.stateOfFormation]
              : []),
            items.data.spec.platform.value.owner.profile.value.dateOfFormation,
            items.data.spec.platform.value.owner.profile.value.taxIdentifier,
            items.data.spec.platform.value.owner.profile.value.natureOfBusiness,
            items.data.spec.platform.value.owner.profile.value.w9.isSubjectToBackupWithholding,
            items.data.spec.platform.value.owner.profile.value.w9.certification,
            items.data.spec.platform.value.owner.profile.value.w9.foreignOwners,
            items.data.spec.platform.value.owner.profile.value.w9.exemptPayeeCode,
            items.data.spec.platform.value.owner.profile.value.w9.signed,
          ]
        : []),
    ];
  }

  if ('customer' in items.data.spec) {
    return [
      { ...items.data.spec.customer.value.registration.typeName, title: 'Registration' },
      ...match(items.data.spec.customer.value)
        .with({ registration: { individual: P.not(P.nullish) } }, (data) => [
          data.profileSummaries,
          data.registration.individual.value.consent.customerConsent,
          data.registration.individual.value.consent.signed,
        ])
        .with({ registration: { organization: P.not(P.nullish) } }, (data) => [
          data.profileSummaries,
          data.registration.organization.value.consent.customerConsent,
          data.registration.organization.value.consent.signed,
        ])
        .with({ registration: { ira: P.not(P.nullish) } }, (data) => [
          data.profileSummaries,
          data.registration.ira.value.consent.customerConsent,
          data.registration.ira.value.consent.signed,
        ])
        .with({ registration: { rothIra: P.not(P.nullish) } }, (data) => [
          data.profileSummaries,
          data.registration.rothIra.value.consent.customerConsent,
          data.registration.rothIra.value.consent.signed,
        ])
        .with({ registration: { sepIra: P.not(P.nullish) } }, (data) => [
          data.profileSummaries,
          data.registration.sepIra.value.employerProfile || data.registration.sepIra.value.employerProfileId,
          data.registration.sepIra.value.consent.customerConsent,
          data.registration.sepIra.value.consent.signed,
        ])
        .with({ registration: { simpleIra: P.not(P.nullish) } }, (data) => [
          data.profileSummaries,
          data.registration.simpleIra.value.employerProfile || data.registration.simpleIra.value.employerProfileId,
          data.registration.simpleIra.value.consent.customerConsent,
          data.registration.simpleIra.value.consent.signed,
        ])
        .with({ registration: { custodial: P.not(P.nullish) } }, (data) => [
          { ...data.registration.custodial.value.rights.typeName, title: 'Rights' },
          data.registration.custodial.value.customizedTerminationAge,
          ...match(data.registration.custodial.value.governingEntity)
            .with({ usState: P.not(P.nullish) }, (usState) => [usState.usState.value.usState])
            .otherwise(() => []),
          { ...data.profileSummaries },
          data.registration.custodial.value.profile || data.registration.custodial.value.profileId,
          data.registration.custodial.value.custodianProfile || data.registration.custodial.value.custodianProfileId,
          data.registration.custodial.value.consent.customerConsent,
          data.registration.custodial.value.consent.signed,
        ])
        .with({ registration: { joint: P.not(P.nullish) } }, (data) => [
          { ...data.registration.joint.value.rights.typeName, title: 'Rights' },
          { ...data.profileSummaries },
          data.registration.joint.value.primaryProfile || data.registration.joint.value.primaryProfileId,
          data.registration.joint.value.consent.customerConsent,
          data.registration.joint.value.consent.signed,
        ])
        .otherwise(() => []),
    ];
  }

  return [];
}

function getListItems(state: IxcRegistrationV1AccountState | undefined) {
  const items = getRegistrationAccountStateListItems(state);

  return [items.statusPill, items.accountId, items.data.displayId, items.programId, ...getPurposeListItems(items), items.data.accountingGroup];
}

function AccountDetailOverview() {
  const { accountId } = Route.useParams();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.account), [data?.account]);

  return (
    <VerticalStack gap={4}>
      <RegistrationAccountOverviewTabbedNavigation accountId={accountId} />

      <AppReadonlyList isPending={showPending} items={listItems} />
    </VerticalStack>
  );
}
